import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatMoney, formatNumber } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode?: string | null;
}

/** `GET /wallet/cash-rewards` — the cash a wallet redemption owes (CR-WALLET-001 §6.3). */
export interface PointsReward {
  id: string;
  status: 'PENDING' | 'PAID' | 'CANCELLED';
  amount: string;
  currency: string;
  /** "YYYY-MM": the first payroll month that pays it. */
  effectiveMonth: string;
  settledVia: 'PAYROLL' | 'MANUAL' | null;
  paymentReference: string | null;
  paidAt: string | null;
  paidBy: Person | null;
  cancelledAt: string | null;
  cancelledBy: Person | null;
  cancellationReason: string | null;
  createdAt: string;
  employee: Person;
  redemption: { id: string; pointsSpent: number; cashBundleCount: number | null; createdAt: string };
  payroll: { id: string; year: number; month: number; status: string } | null;
  can: { markPaid: boolean; cancel: boolean };
}

interface RewardList extends Paginated<PointsReward> {
  counts: Record<'PENDING' | 'PAID' | 'CANCELLED', number>;
  pendingTotals: Array<{ currency: string; amount: string; count: number }>;
}

const TABS = ['PENDING', 'PAID', 'CANCELLED', 'all'] as const;

/**
 * Finance › Payroll › Points rewards: what employees redeemed wallet points for
 * in cash. Each is paid with the salary — the next payroll calculated for its
 * month or later carries it, and marking that payroll paid pays it — or here, by
 * hand, once the money went out another way. Cancelling one gives its points
 * back, once. `payroll.manage`.
 */
export function PointsRewardsPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as (typeof TABS)[number]) : 'PENDING';
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['points-rewards', tab, page, q],
    queryFn: () =>
      api.get<RewardList>('/wallet/cash-rewards', {
        status: tab === 'all' ? undefined : tab,
        page,
        pageSize: 25,
        q: q.trim() || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const month = (year: number, m: number) => `${MONTH_NAMES[locale][m - 1]} ${year}`;
  const monthOf = (yyyyMm: string) => month(Number(yyyyMm.slice(0, 4)), Number(yyyyMm.slice(5, 7)));

  return (
    <>
      <PageHeader title={t('pointsRewards.title')} subtitle={t('pointsRewards.subtitle')} />

      {data && data.pendingTotals.length > 0 && (
        <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {data.pendingTotals.map((total) => (
            <Metric
              key={total.currency}
              label={t('pointsRewards.pendingTotal', { currency: total.currency })}
              value={formatMoney(total.amount, total.currency, locale)}
              hint={total.count === 1 ? t('pointsRewards.pendingCountOne') : t('pointsRewards.pendingCount', { n: formatNumber(total.count, locale) })}
              tone="warning"
              icon={<Coins className="h-4 w-4" />}
            />
          ))}
        </div>
      )}

      <Tabs
        active={tab}
        onChange={(id) => {
          setPage(1);
          setParams(id === 'PENDING' ? {} : { tab: id }, { replace: true });
        }}
        tabs={TABS.map((id) => ({
          id,
          label: t(`pointsRewards.tab.${id}`),
          count: id === 'all' ? undefined : data?.counts?.[id],
        }))}
      />

      <Card padded={false} className="mt-4">
        <div className="border-b hairline p-3">
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder={t('pointsRewards.search')}
            aria-label={t('pointsRewards.search')}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error || !data ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState icon={<Coins className="h-8 w-8" />} title={t('pointsRewards.none')} hint="" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('pointsRewards.employee')}</Th>
                  <Th>{t('pointsRewards.redeemed')}</Th>
                  <Th align="end">{t('pointsRewards.points')}</Th>
                  <Th align="end">{t('pointsRewards.amount')}</Th>
                  <Th>{t('pointsRewards.where')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id}>
                    <Td>
                      <span className="block">{localized(r.employee)}</span>
                      {r.employee.employeeCode && <span className="block font-mono text-caption text-faint">{r.employee.employeeCode}</span>}
                    </Td>
                    <Td className="whitespace-nowrap tabular text-muted">{formatDate(r.createdAt, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap tabular">
                      <span className="block">{formatNumber(r.redemption.pointsSpent, locale)}</span>
                      {r.redemption.cashBundleCount != null && (
                        <span className="block text-caption text-faint">
                          {r.redemption.cashBundleCount === 1
                            ? t('pointsRewards.bundleOne')
                            : t('pointsRewards.bundles', { n: formatNumber(r.redemption.cashBundleCount, locale) })}
                        </span>
                      )}
                    </Td>
                    <Td align="end" className="whitespace-nowrap tabular font-semibold">
                      {formatMoney(r.amount, r.currency, locale)}
                    </Td>
                    <Td className="text-body-sm text-muted">
                      {r.status === 'PAID' ? (
                        <>
                          {r.settledVia === 'PAYROLL' && r.payroll
                            ? t('pointsRewards.paidWithPayroll', { month: month(r.payroll.year, r.payroll.month) })
                            : t('pointsRewards.paidByHand')}
                          {r.paymentReference && (
                            <span className="block text-caption text-faint" dir="auto">
                              {t('pointsRewards.reference', { ref: r.paymentReference })}
                            </span>
                          )}
                          {r.paidAt && (
                            <span className="block text-caption text-faint">
                              {formatDate(r.paidAt, locale)}
                              {r.paidBy && ` · ${localized(r.paidBy)}`}
                            </span>
                          )}
                        </>
                      ) : r.status === 'CANCELLED' ? (
                        <>
                          {r.cancelledBy && t('pointsRewards.cancelledBy', { name: localized(r.cancelledBy) })}
                          {r.cancellationReason && (
                            <span className="block max-w-xs truncate text-caption text-faint" dir="auto" title={r.cancellationReason}>
                              {r.cancellationReason}
                            </span>
                          )}
                        </>
                      ) : r.payroll ? (
                        t('pointsRewards.onPayroll', {
                          month: month(r.payroll.year, r.payroll.month),
                          status: enumLabel(r.payroll.status, locale),
                        })
                      ) : (
                        t('pointsRewards.notOnPayroll', { month: monthOf(r.effectiveMonth) })
                      )}
                    </Td>
                    <Td>
                      <Badge tone={statusTone(r.status)}>{t(`pointsRewards.status.${r.status}`)}</Badge>
                    </Td>
                    <Td align="end" className="whitespace-nowrap">
                      <span className="inline-flex gap-1.5">
                        {r.can.markPaid && (
                          <Button size="sm" onClick={() => navigate(`/points-rewards/${r.id}/mark-paid`)}>
                            {t('pointsRewards.markPaid')}
                          </Button>
                        )}
                        {r.can.cancel && (
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/points-rewards/${r.id}/cancel`)}>
                            {t('pointsRewards.cancel')}
                          </Button>
                        )}
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
